//@ts-ignore
import useResizeAware from "react-resize-aware";

import { ChallengeRow, ChallengeRowLarge, HeroBannerSection, NavSection } from "@Components/game";
import { BybCategoryButton } from "@Components/game/common";
import ContainerWrapper from "@Components/generic/container-wrapper";
import { Actions, Events } from "@Context/common/general/context";
import { faCheckCircle, faGrid2, faPlayCircle, faRepeatAlt } from "@fortawesome/pro-solid-svg-icons";
import useGlobalContext from "@hooks/common/general/use-global-context";
import { Grid, Stack, useTheme } from "@mui/material";

// Swiper styles
import ScrollfulContainer from "@Components/generic/scrollful-container";
import { ChallengeLeagueTags, ChallengeStatus, VRHeaderButtonType } from "@Enums/index";
import useDemoAccount from "@hooks/common/user/use-demo-account";
import useI18n from "@i18n";
import { useMount } from "react-use";
import { consolidateTagsForUser, demoTag } from "./utils";

const PageId = "CHALLENGES";

const Challenges = () => {
  const { globalState, dispatch } = useGlobalContext();
  const { i18n } = useI18n();
  const theme = useTheme();
  const [resizeListener, sizes] = useResizeAware();
  const { isDemo } = useDemoAccount();
  const tags = consolidateTagsForUser(globalState, isDemo);

  useMount(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("challenges.page.title") });
    dispatch({
      type: Actions.ProgressionActions.GetProgressionConfig,
      payload: {
        cache_time: [60, "mins"],
        session_only: true,
        disableLoaders: true,
      },
    });
  });

  return (
    <ContainerWrapper id={PageId} sx={{ overflow: "visible" }} fullHeight>
      <ScrollfulContainer maxWidth={false} disableGutters sx={{ padding: theme.spacing(0, 0, 6) }}>
        <Stack spacing={6}>
          <HeroBannerSection />

          <NavSection title={i18n("challenges.subheader.continue")} icon={faPlayCircle} headerSx={{ padding: theme.spacing(0, 6) }}>
            <ChallengeRow
              size="medium"
              filters={{
                statuses: [ChallengeStatus.FAILED, ChallengeStatus.NOT_STARTED, ChallengeStatus.IN_PROGRESS],
              }}
              limit={3}
              overflow={false}
              tag={isDemo ? demoTag.id : undefined}
              excludedTags={!isDemo ? [ChallengeLeagueTags.QA, ChallengeLeagueTags.DEVELOPMENT, ChallengeLeagueTags.DEMO] : undefined}
            />
          </NavSection>

          <NavSection title={i18n("challenges.subheader.categories")} icon={faGrid2} sx={{ padding: theme.spacing(0, 6) }}>
            <Stack>
              <Grid container spacing={3}>
                {tags.map((tag, index) => (
                  <Grid key={index} item xs={3}>
                    <BybCategoryButton variant="contained" size="large" fullWidth tag={tag}>
                      {i18n(`tag.${tag.id}`)}
                      {/* prevent multiple rerenders by only setting once */}
                      {index === tags.length - 1 && resizeListener}
                    </BybCategoryButton>
                  </Grid>
                ))}
                {sizes?.height && (
                  <Grid item xs={3}>
                    <BybCategoryButton variant="contained" size="large" color="primary" fullWidth height={sizes?.height}>
                      {i18n("challenges.categories.all")}
                    </BybCategoryButton>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </NavSection>

          <NavSection
            title={i18n("challenges.subheader.drills")}
            icon={faRepeatAlt}
            sx={{ position: "relative", "> *:first-of-type": { padding: theme.spacing(0, 6) } }}
          >
            <ChallengeRowLarge tag="DRILL" padding={theme.spacing(6)} />
          </NavSection>

          <NavSection title={i18n("challenges.subheader.recently.played")} icon={faCheckCircle} headerSx={{ padding: theme.spacing(0, 6) }}>
            <ChallengeRow
              size="small"
              filters={{
                statuses: [ChallengeStatus.COMPLETED, ChallengeStatus.FAILED],
                max_rating: globalState.user.userRating!,
              }}
              orderBy={[{ field: "last_played_at", direction: "desc" }]}
              limit={7}
            />
          </NavSection>
        </Stack>
      </ScrollfulContainer>
    </ContainerWrapper>
  );
};

export default Challenges;
